/**
 * V36.41 — ReferentielsManager
 *
 * Composant CRUD reutilisable pour gerer un referentiel (territoires,
 * types-entreprise, types-demande) cote Super Admin.
 *
 * Props :
 *  - kind : "territoires" | "types-entreprise" | "types-demande"
 *  - title : titre affiche (ex: "Territoires")
 *  - accentColor : "emerald" | "sky" | "purple" | "amber"
 *  - onChange : callback(items) appele apres chaque CRUD reussi
 *  - extraFields : array de definitions de champs supplementaires
 *      [{ key: 'tva', label: 'TVA (%)', type: 'number', placeholder: '20', metaKey: true }]
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription
} from './ui/dialog';
import { Plus, Edit2, Copy, Trash2, Save, Layers, Lock, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCENT_CLASSES = {
  emerald: { border: 'border-emerald-500', bg: 'bg-emerald-50/50', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800', btn: 'bg-emerald-600 hover:bg-emerald-700' },
  sky:     { border: 'border-sky-500',     bg: 'bg-sky-50/50',     text: 'text-sky-700',     badge: 'bg-sky-100 text-sky-800',         btn: 'bg-sky-600 hover:bg-sky-700' },
  purple:  { border: 'border-purple-500',  bg: 'bg-purple-50/50',  text: 'text-purple-700',  badge: 'bg-purple-100 text-purple-800',   btn: 'bg-purple-600 hover:bg-purple-700' },
  amber:   { border: 'border-amber-500',   bg: 'bg-amber-50/50',   text: 'text-amber-700',   badge: 'bg-amber-100 text-amber-800',     btn: 'bg-amber-600 hover:bg-amber-700' },
  indigo:  { border: 'border-indigo-500',  bg: 'bg-indigo-50/50',  text: 'text-indigo-700',  badge: 'bg-indigo-100 text-indigo-800',   btn: 'bg-indigo-600 hover:bg-indigo-700' },
};

export default function ReferentielsManager({ kind, title, accentColor = 'emerald', onChange, extraFields = [] }) {
  const { getAuthHeader } = useAuth();
  const acc = ACCENT_CLASSES[accentColor] || ACCENT_CLASSES.emerald;

  // V37.24 — Refs stables pour eviter les boucles de re-render infinies.
  // `getAuthHeader` (depuis AuthContext) et `onChange` (prop inline du parent)
  // sont recrees a chaque render -> sans refs, `useCallback` ci-dessous voit
  // ses deps changer en permanence -> `useEffect` re-execute fetchItems ->
  // setItems -> render -> onChange -> parent render -> nouvelle ref -> boucle.
  const getAuthHeaderRef = useRef(getAuthHeader);
  const onChangeRef = useRef(onChange);
  useEffect(() => { getAuthHeaderRef.current = getAuthHeader; }, [getAuthHeader]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: '', description: '', is_active: true, meta: {}, sort_order: 0 });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/referentiels/${kind}`, { headers: getAuthHeaderRef.current() });
      const list = res.data?.items || [];
      setItems(list);
      onChangeRef.current?.(list);
    } catch (e) {
      toast.error(`Erreur chargement ${title}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [kind, title]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => {
    setEditing(null);
    setForm({ label: '', description: '', is_active: true, meta: {}, sort_order: items.length });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      label: item.label,
      description: item.description || '',
      is_active: item.is_active !== false,
      meta: item.meta || {},
      sort_order: item.sort_order || 0,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.label?.trim()) {
      toast.error('Le libelle est requis');
      return;
    }
    try {
      const payload = { ...form };
      if (editing) {
        await axios.put(`${API}/admin/referentiels/${kind}/${editing.id}`, payload, { headers: getAuthHeader() });
        toast.success('Modifie');
      } else {
        await axios.post(`${API}/admin/referentiels/${kind}`, payload, { headers: getAuthHeader() });
        toast.success('Cree');
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const handleDuplicate = async (item) => {
    try {
      await axios.post(`${API}/admin/referentiels/${kind}/${item.id}/duplicate`, {}, { headers: getAuthHeader() });
      toast.success(`"${item.label}" duplique`);
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const handleDelete = async (item) => {
    const msg = item.is_system
      ? `"${item.label}" est une entite systeme. Confirmer sa DESACTIVATION ? (non supprimable)`
      : `Supprimer definitivement "${item.label}" ?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.delete(`${API}/admin/referentiels/${kind}/${item.id}`, { headers: getAuthHeader() });
      if (res.data?.disabled) {
        toast.success(`"${item.label}" desactive`);
      } else {
        toast.success(`"${item.label}" supprime`);
      }
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await axios.put(`${API}/admin/referentiels/${kind}/${item.id}`, {
        label: item.label,
        description: item.description,
        meta: item.meta,
        sort_order: item.sort_order,
        is_active: !item.is_active,
      }, { headers: getAuthHeader() });
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className={`rounded-xl border-l-4 ${acc.border} ${acc.bg} p-4 space-y-3`} data-testid={`referentiels-${kind}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Layers className={`h-5 w-5 ${acc.text}`} />
          <h3 className={`text-sm font-bold uppercase tracking-wider ${acc.text}`}>{title}</h3>
          <Badge variant="outline" className="text-xs">{items.length}</Badge>
        </div>
        <Button size="sm" onClick={openCreate} className={`${acc.btn} text-white`} data-testid={`add-${kind}`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin"/> Chargement...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400 italic">Aucun item.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map((item) => (
            <div
              key={item.id}
              className={`bg-white border ${item.is_active ? 'border-slate-200' : 'border-slate-200 opacity-60'} rounded-lg p-2.5 flex items-start gap-2 hover:shadow-sm transition-all`}
              data-testid={`item-${kind}-${item.value}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-sm text-slate-800 truncate">{item.label}</span>
                  {item.is_system && (
                    <span title="Entite systeme : non supprimable">
                      <Lock className="h-3 w-3 text-slate-400" />
                    </span>
                  )}
                  {!item.is_active && (
                    <Badge variant="outline" className="text-[10px] text-slate-500">Inactif</Badge>
                  )}
                </div>
                <code className="text-[10px] text-slate-500 font-mono">{item.value}</code>
                {item.description && (
                  <p className="text-xs text-slate-600 mt-0.5 truncate" title={item.description}>{item.description}</p>
                )}
              </div>
              <div className="flex flex-col gap-1 items-end">
                <Switch
                  checked={item.is_active}
                  onCheckedChange={() => handleToggleActive(item)}
                  className="scale-75"
                  data-testid={`toggle-${kind}-${item.value}`}
                />
                <div className="flex gap-0.5">
                  <button onClick={() => openEdit(item)} className="text-slate-500 hover:text-blue-600 p-1" title="Modifier" data-testid={`edit-${kind}-${item.value}`}>
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => handleDuplicate(item)} className="text-slate-500 hover:text-purple-600 p-1" title="Dupliquer" data-testid={`duplicate-${kind}-${item.value}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => handleDelete(item)} className="text-slate-500 hover:text-red-600 p-1" title="Supprimer / Desactiver" data-testid={`delete-${kind}-${item.value}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog edition */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier' : 'Nouveau'} — {title}</DialogTitle>
            <DialogDescription>
              {editing ? `Modification de "${editing.label}"` : `Ajouter un nouvel item au referentiel ${title}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Libelle *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Ex: Polynesie Francaise"
                data-testid="ref-form-label"
              />
              {editing?.value && (
                <p className="text-[10px] text-slate-400 font-mono">slug : {editing.value} (non modifiable)</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Description</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description courte..."
                data-testid="ref-form-description"
              />
            </div>

            {extraFields.map((field) => (
              <div key={field.key} className="space-y-1.5">
                <Label className="text-xs font-semibold">{field.label}</Label>
                <Input
                  type={field.type || 'text'}
                  step={field.step || '1'}
                  value={field.metaKey ? (form.meta?.[field.key] ?? '') : (form[field.key] ?? '')}
                  onChange={(e) => {
                    const v = field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
                    if (field.metaKey) {
                      setForm({ ...form, meta: { ...form.meta, [field.key]: v } });
                    } else {
                      setForm({ ...form, [field.key]: v });
                    }
                  }}
                  placeholder={field.placeholder}
                  data-testid={`ref-form-field-${field.key}`}
                />
              </div>
            ))}

            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
              <div>
                <Label className="text-xs font-semibold">Statut</Label>
                <p className="text-[11px] text-slate-500">Actif = visible dans les selecteurs.</p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="ref-form-active-switch"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Ordre d&apos;affichage</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                className="font-mono"
                data-testid="ref-form-sort-order"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} className={`${acc.btn} text-white`} data-testid="ref-form-save-btn">
              <Save className="h-4 w-4 mr-1.5" />
              {editing ? 'Mettre a jour' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
